import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  Badge,
  Button,
  DataTable,
  Label,
  MultiSelect,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectionActionsBar,
  type DataTableColumn,
  type DataTableSortState,
  type SelectionAction,
} from '@atrvd/saas-ui'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { useFacilities, usePacks } from '@/hooks/useBanking'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import {
  useAssignAccountFacility,
  useConnectionAccounts,
  useDeleteAccounts,
  useDisconnectAccounts,
  useReconnectAccounts,
  useSetAccountType,
  useSetIncludeInCashPosition,
} from '@/hooks/useBankConnections'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '@/hooks/useDebouncedValue'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import {
  FACILITY_SCOPE_COPY,
  facilityAccountsEmpty,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { CashPositionToggle } from '@/components/financial-accounts/CashPositionToggle'
import { ReadOnlyActionMenu, ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'
import { facilityOptionsFor } from '@/components/manual-banks/manualBanks.utils'
import type {
  ConnectionAccountRow,
  ConnectionAccountSortColumn,
  ConnectionAccountStatusFilter,
} from '@/lib/db-tenant'
import { CONNECTION_ACCOUNTS_DEFAULT_SORT, CONNECTION_ACCOUNT_SORT_COLUMNS_LIST } from '@/lib/db-tenant'
import {
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TYPE_CHOICES,
  CONNECTION_COPY,
  EM_DASH,
  NO_PACK,
  UNASSIGNED_FACILITY,
  UNASSIGNED_FACILITY_LABEL,
  accountConfirmLabel,
  accountTypeOptions,
  facilityFilterOptions,
  facilityKeepsAccountShown,
  formatAccountNumber,
  isCashPositionLocked,
  packFilterOptions,
  pruneFacilitySelection,
  toFilterEntries,
} from './connectionManagement.utils'

/** Runtime guard confining a DataTable column key to the RPC's whitelist. */
const SORTABLE = new Set<string>(CONNECTION_ACCOUNT_SORT_COLUMNS_LIST)

/**
 * Which confirmable action the dialog is asking about, over which rows, and
 * WHERE IT CAME FROM.
 *
 * `scope` is not cosmetic: one dialog instance serves both the bulk bar and
 * every row's kebab, and what happens to the selection afterwards differs
 * between the two (see `confirmPending`). A bulk action consumed the selection,
 * so the selection goes; a row action is about ONE row, which may not even be
 * ticked, so the user's unrelated selection has to survive it.
 */
type PendingAction = {
  kind: 'disconnect' | 'reconnect' | 'delete'
  ids: string[]
} & (
  | {
      scope: 'row'
      /**
       * How the dialog names the account (DCP-404). Captured when the kebab is
       * opened rather than looked up from the rows on render: the row leaves
       * the list on success while the dialog is still closing.
       */
      label: string
    }
  | { scope: 'bulk' }
)

/** Props for {@link ConnectionAccountsTable}. */
interface ConnectionAccountsTableProps {
  connectionId: string
  /**
   * The facility page the connection was opened from; null org-wide. Scoped,
   * the table shows only that facility's accounts, without the Pack column and
   * the Pack / Facility filters; an inactive facility makes it read-only.
   */
  scope: FacilityFinancialAccountsScope | null
  /** Rendered beside the "Accounts & cards" heading (the facility page's add button). */
  headingAction?: ReactNode
}

/**
 * The accounts of one bank connection, with inline editing, filters and bulk
 * actions (DCP-324, Screen 2).
 *
 * NUMBERED PAGES ON THE SERVER. `useConnectionAccounts` reads one page
 * (`limit` + `offset`, 20/50/100 via `useTablePagination`) with the filter-set
 * total. This screen edits the very columns it sorts and filters on: after an
 * edit the refetched page may show the row elsewhere, but the current page
 * stays (clamped to the new last page). Offset rather than the RPC's keyset
 * cursor because DCP-390 gives every table numbered pages: a row re-sorted
 * between two page reads (an edit, a sync) can be skipped or shown twice while
 * paging; the `id` tiebreak keeps the order stable otherwise.
 *
 * Deliberately NOT inside a `Modal` and NOT using `stickyHeader` +
 * `maxHeight`: both would nest a scroll container inside the page's, which is
 * the bug DCP has fixed four times (DCP-313/312/336/332). The page scrolls.
 *
 * BULK ACTIONS USE THE LIBRARY'S STANDALONE BAR, not DataTable's `bulkActions`.
 * Two of the four actions ("Assign facility", "Set account type") need the user
 * to pick a VALUE before it is applied to the selection, and `BulkAction` has no
 * shape for that — `SelectionActionsBar` does.
 */
export function ConnectionAccountsTable({
  connectionId,
  scope,
  headingAction,
}: ConnectionAccountsTableProps) {
  /** An inactive facility's page: shown, never changed (ADR 0016 exception). */
  const readOnly = scope?.readOnly ?? false

  // --- Filters (AC#10/#11/#12) --------------------------------------------
  /**
   * What the search box SHOWS. Updated on every keystroke, so the input itself
   * stays fully responsive while the QUERY trails behind it.
   */
  const [search, setSearch] = useState('')
  /** The trimmed term the query is keyed on — `search`, {@link SEARCH_DEBOUNCE_MS} later. */
  const searchTerm = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  // The rows on screen still answer the previous term.
  const searchSettling = search.trim() !== searchTerm
  /** AC#10: `connected` is the default — see the data layer's note on why. */
  const [status, setStatus] = useState<ConnectionAccountStatusFilter>('connected')
  /** Raw MultiSelect values; may contain the {@link NO_PACK} sentinel. */
  const [packFilter, setPackFilter] = useState<string[]>([])
  /** Raw MultiSelect values; may contain the {@link UNASSIGNED_FACILITY} sentinel. */
  const [facilityFilter, setFacilityFilter] = useState<string[]>([])
  const [sortState, setSortState] = useState<DataTableSortState[]>([])

  /**
   * Selected row ids across ALL pages, as on Daily Banking: the table holds one
   * page, so the ids are the only record of rows ticked elsewhere. Kept through
   * paging, page size and sorting — the same rows, on another page or in
   * another order. The bar counts every id and every bulk action sends every
   * id; the header checkbox (saas-ui) toggles the CURRENT page and leaves the
   * other ids alone.
   *
   * SCOPED TO THE CURRENT FILTER. With one page loaded the ids cannot be
   * checked against the whole filter set, so stale ids are pruned at the
   * events that make them stale on this screen instead:
   * - every filter change drops the selection (the four handlers below) —
   *   otherwise the bar's Disconnect and the irreversible "Delete permanently"
   *   (AC#17/AC#18) would reach rows the user can no longer see;
   * - a successful bulk action clears it, a successful row action drops its row
   *   (`confirmPending`);
   * - an inline Facility change drops its row when the new facility takes it
   *   out of the facility scope or the Facility / Pack filters
   *   (`facilityKeepsAccountShown`).
   * A row that leaves the filter set through a write made elsewhere (another
   * tab, a sync) stays selected — counted in the bar — until one of those
   * events; the table is not re-read in full to catch it.
   */
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set())
  /** The confirmable action awaiting the dialog's confirm. */
  const [pending, setPending] = useState<PendingAction | null>(null)

  /**
   * Drop the whole selection.
   *
   * Returns the SAME Set when it is already empty, so the filter handlers can
   * call it unconditionally — including on every keystroke in the search box —
   * without queueing a state update, and therefore a re-render, that changes
   * nothing.
   */
  const clearSelection = useCallback(
    () => setSelectedIds((current) => (current.size === 0 ? current : new Set())),
    [],
  )

  /**
   * Drop only the given ids from the selection, leaving the rest ticked.
   *
   * What a SINGLE-ROW action needs: the row it acted on may or may not be part
   * of a selection the user built for something else, and either way that
   * selection is none of its business. Ids are compared as strings because the
   * Set is typed `string | number` by `DataTable`'s selection API while account
   * ids are uuids.
   *
   * @param ids - The account ids the action consumed
   */
  const deselect = useCallback((ids: string[]) => {
    setSelectedIds((current) => {
      const consumed = new Set(ids)
      const next = new Set(Array.from(current).filter((id) => !consumed.has(String(id))))
      return next.size === current.size ? current : next
    })
  }, [])

  /**
   * THE FOUR FILTER HANDLERS ALL CLEAR THE SELECTION. See `selectedIds`: the
   * selection is a set of ids with no memory of which query produced them, so
   * surviving a filter change is what would let a bulk action reach a row that
   * is no longer on screen. Clearing is also the honest reading of the gesture
   * — the user asked for a different set of rows.
   *
   * The search box clears on the KEYSTROKE rather than when the debounced term
   * reaches the query: the rows are about to change either way, and leaving the
   * bar alive during the debounce window would keep exactly the hazard above
   * open for 300ms. For the same reason the rows are not selectable until the
   * typed term reaches the query (`searchSettling`).
   */
  const handleSearchChange = useCallback(
    (next: string) => {
      setSearch(next)
      clearSelection()
    },
    [clearSelection],
  )

  const handleStatusChange = useCallback(
    (next: ConnectionAccountStatusFilter) => {
      setStatus(next)
      clearSelection()
    },
    [clearSelection],
  )

  const handleFacilityFilterChange = useCallback(
    (next: string[]) => {
      setFacilityFilter(next)
      clearSelection()
    },
    [clearSelection],
  )

  const { data: facilities } = useFacilities()
  // Same cache entry, unfiltered: a row's Facility select also offers the
  // account's own facility when that one is inactive (see `facilityOptionsFor`).
  const { data: allFacilities } = useFacilities({ activeOnly: false })
  const { data: packs } = usePacks()

  const activeFacilities = useMemo(() => facilities ?? [], [facilities])

  const packOptions = useMemo(() => packFilterOptions(packs ?? []), [packs])
  // AC#12: once packs are picked the Facility filter offers only their
  // facilities. Anything else could not match a row that already passed the
  // pack filter.
  const facilityOptions = useMemo(
    () => facilityFilterOptions(activeFacilities, packFilter),
    [activeFacilities, packFilter],
  )

  /**
   * Change the Pack selection and drop any facility selection the narrowed
   * option list no longer offers — otherwise a facility chosen BEFORE a pack
   * would stay in the query while vanishing from its dropdown, i.e. an
   * invisible filter with no way to clear it.
   *
   * The fourth of the filter handlers, so it releases the row selection too;
   * see `handleSearchChange` for why. It lives down here rather than beside the
   * other three because it needs `activeFacilities` to narrow the facility
   * options.
   */
  const handlePackFilterChange = useCallback(
    (next: string[]) => {
      setPackFilter(next)
      setFacilityFilter((current) =>
        pruneFacilitySelection(current, facilityFilterOptions(activeFacilities, next)),
      )
      clearSelection()
    },
    [activeFacilities, clearSelection],
  )

  const activeSort = sortState[0]
  const sortColumn =
    activeSort && activeSort.direction !== null && SORTABLE.has(activeSort.columnKey)
      ? (activeSort.columnKey as ConnectionAccountSortColumn)
      : undefined
  const sortDirection =
    activeSort && activeSort.direction !== null && sortColumn ? activeSort.direction : undefined
  // The order the rows really arrive in, the data layer's default without a
  // sort (column keys are the RPC's sort columns). Shown on the headers only,
  // so the query is unchanged.
  const shownSort: DataTableSortState[] = [
    {
      columnKey: sortColumn ?? CONNECTION_ACCOUNTS_DEFAULT_SORT.column,
      direction: sortDirection ?? CONNECTION_ACCOUNTS_DEFAULT_SORT.direction,
    },
  ]

  /** Reset search and every filter to the defaults ("Clear filters"). */
  const clearFilters = useCallback(() => {
    setSearch('')
    setStatus('connected')
    setPackFilter([])
    setFacilityFilter([])
    clearSelection()
  }, [clearSelection])

  // The total arrives with the page, so it goes through `forTotal` below. Back
  // to page 1 on the debounced term, not the keystroke: the rows change when
  // the query does.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify([status, searchTerm, packFilter, facilityFilter, sortColumn, sortDirection]),
  })

  const accounts = useConnectionAccounts(connectionId, {
    status,
    search: searchTerm.length > 0 ? searchTerm : undefined,
    // Scoped, the facility is fixed and the Pack / Facility filters are gone.
    packIds: scope ? undefined : toFilterEntries(packFilter, NO_PACK),
    facilityIds: scope ? [scope.facilityId] : toFilterEntries(facilityFilter, UNASSIGNED_FACILITY),
    sortColumn,
    sortDirection,
    pageSize: pagination.pageSize,
    offset: pagination.offset,
  })
  // `knownTotal` outlives a failed page, so the badge and the footer keep the
  // set's count and the user can step back or retry.
  const { dataTablePagination, knownTotal } = pagination.forTotal({
    total: accounts.totalCount,
    isFetching: accounts.isFetching,
  })
  const tableState = tableQueryState(accounts, accounts.rows.length)
  const errorDisplay = queryErrorDisplay(tableState, accounts.rows.length)

  // --- Mutations ----------------------------------------------------------
  const setAccountType = useSetAccountType()
  const assignFacility = useAssignAccountFacility()
  const setIncludeInCashPosition = useSetIncludeInCashPosition()
  const disconnectAccounts = useDisconnectAccounts()
  const reconnectAccounts = useReconnectAccounts()
  const deleteAccounts = useDeleteAccounts()

  /** Every selected id (all pages) as a plain array, for the mutation payloads. */
  const selectedIdList = useMemo(() => Array.from(selectedIds, String), [selectedIds])

  /**
   * Run a bulk mutation and clear the selection ONLY on success. A failed write
   * leaves the rows selected so the user can retry without re-picking them —
   * clearing regardless would make a failure look like a completed action.
   *
   * @param run - The mutation, returning its promise
   */
  const runBulk = useCallback(
    async (run: () => Promise<unknown>) => {
      try {
        await run()
        clearSelection()
      } catch {
        // The hook already surfaced the message; the selection is kept.
      }
    },
    [clearSelection],
  )

  /**
   * Change the type of one or many accounts, forcing them OUT of the Cash
   * Position when the new type is a credit card (AC#15 / DCP-371).
   *
   * WHY THIS IS TWO WRITES. `useSetAccountType` writes `account_type` and
   * nothing else, so an account that already carried
   * `include_in_cash_position: true` — a Checking account being reclassified —
   * KEPT that flag when its type flipped to `credit_card`. The row then
   * rendered a disabled toggle reading "Yes" (AC#15 locks the control for a
   * card), and every consumer that sums the flagged accounts counted a
   * liability as cash: exactly the DCP-371 defect this project removed, now
   * reachable from this screen. The flag is therefore cleared explicitly, and
   * from BOTH paths that can set the type — this row's inline Select and the
   * bulk "Set account type" action, which both come through here.
   *
   * THE CLEAR GOES FIRST, and the type write never runs if it fails. Either
   * order can be interrupted by a failing request, so what decides the order is
   * which half-applied state is survivable:
   * - clear → type: a failed type write leaves a Checking account merely
   *   EXCLUDED from Cash Position. Its toggle is still enabled, so the user can
   *   put it back, and "no credit card is ever included" never breaks.
   * - type → clear: a failed clear leaves a credit card flagged INTO the Cash
   *   Position with its toggle now DISABLED — both the bug being fixed and a
   *   state the UI offers no way out of.
   *
   * The clear is unconditional rather than limited to rows whose loaded
   * `include_in_cash_position` is true: the loaded row is a CACHE (another tab,
   * or this table's own earlier toggle, can have set the flag since it was
   * fetched), so skipping on it could leave a `true` behind in the database.
   * Writing `false` over an already-false row costs one idempotent request and
   * is silent — `useSetIncludeInCashPosition` only toasts on failure.
   *
   * @param ids - The accounts to reclassify
   * @param accountType - The new `account_type`
   * @returns A promise that REJECTS if either write fails, for the caller to handle
   */
  const applyAccountType = useCallback(
    async (ids: string[], accountType: ConnectionAccountRow['account_type']) => {
      if (accountType === 'credit_card') {
        await setIncludeInCashPosition.mutateAsync({ ids, include: false })
      }
      await setAccountType.mutateAsync({ ids, accountType })
    },
    [setAccountType, setIncludeInCashPosition],
  )

  /**
   * The inline Select's type change. Fire-and-forget, like every other inline
   * edit in this table — the sequencing above needs a promise, so the rejection
   * is absorbed here rather than left to become an unhandled one.
   *
   * @param id - The row's account id
   * @param accountType - The newly picked type
   */
  const changeRowAccountType = useCallback(
    (id: string, accountType: ConnectionAccountRow['account_type']) => {
      applyAccountType([id], accountType).catch(() => {
        // Both hooks already surfaced the message as a toast, and the row still
        // shows its pre-change type because nothing was written to the cache.
      })
    },
    [applyAccountType],
  )

  /**
   * The inline Facility select's change. On success the row leaves the
   * selection when the new facility takes it out of the rows shown (see
   * `selectedIds`). `mutateAsync`, not per-call `mutate` callbacks: those fire
   * only for the latest `mutate` of the hook, and two rows can be saving at once.
   *
   * @param id - The row's account id
   * @param value - The picked option: a facility id or {@link UNASSIGNED_FACILITY}
   */
  const changeRowFacility = useCallback(
    (id: string, value: string) => {
      const facilityId = value === UNASSIGNED_FACILITY ? null : value
      assignFacility
        .mutateAsync({ ids: [id], facilityId })
        .then(
          () => {
            const filters = {
              scopeFacilityId: scope?.facilityId ?? null,
              facilityFilter,
              packFilter,
              facilities: allFacilities ?? [],
            }
            if (!facilityKeepsAccountShown(facilityId, filters)) deselect([id])
          },
          () => {
            // The hook already surfaced the message; the row keeps its facility.
          },
        )
    },
    [assignFacility, deselect, scope?.facilityId, facilityFilter, packFilter, allFacilities],
  )

  /**
   * Fire the confirmed action over its rows, then close the dialog.
   *
   * THE SELECTION IS RELEASED ACCORDING TO THE ACTION'S SCOPE, not wholesale. A
   * bulk action consumed the selection, so it all goes; a ROW action only
   * consumed its own row, and wiping the Set there silently threw away a
   * selection the user had built for something else (the row's kebab does not
   * even require the row to be ticked). Only on SUCCESS either way — a failed
   * write leaves the rows selected so the user can retry without re-picking
   * them.
   */
  const confirmPending = useCallback(() => {
    if (!pending) return
    const { ids, scope } = pending
    const done = () => {
      setPending(null)
      if (scope === 'bulk') clearSelection()
      else deselect(ids)
    }
    const fail = () => setPending(null)
    if (pending.kind === 'disconnect') {
      disconnectAccounts.mutate(pending.ids, { onSuccess: done, onError: fail })
      return
    }
    if (pending.kind === 'reconnect') {
      reconnectAccounts.mutate(pending.ids, { onSuccess: done, onError: fail })
      return
    }
    deleteAccounts.mutate(pending.ids, { onSuccess: done, onError: fail })
  }, [pending, disconnectAccounts, reconnectAccounts, deleteAccounts, clearSelection, deselect])

  const confirmBusy =
    disconnectAccounts.isPending || reconnectAccounts.isPending || deleteAccounts.isPending

  const columns = useMemo<DataTableColumn<ConnectionAccountRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        // Anchor column — never hideable. The name is what identifies an
        // account to the reader (most credit cards have no number at all), so
        // hiding it would leave a table of types and balances nobody can place.
        disableHide: true,
        cellClassName: 'font-medium',
        render: (account) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{account.name}</span>
            {/* A disconnected account stays visible under the "All" filter, so
                it needs to say so on the row itself. */}
            {account.status === 'disconnected' && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                Disconnected
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'account_number',
        header: 'Account / Card number',
        sortable: true,
        // LedgerSync gives at most a 4-digit mask, and most credit cards carry
        // no number at all — the em-dash case is the common one here.
        render: (account) => (
          <span className="tabular-nums">{formatAccountNumber(account.account_number)}</span>
        ),
      },
      {
        key: 'account_type',
        header: 'Type',
        headerHint: CONNECTION_COPY.typeHint,
        sortable: true,
        render: (account) => {
          // Busy while EITHER half of `applyAccountType` is in flight for this
          // row. The Cash Position clear runs first and the type write only
          // after it resolves, so watching the type mutation alone would leave
          // the Select live during the first leg — long enough for a second
          // pick to race the first sequence and lose. (It also disables the
          // Select while the row's own Cash Position toggle is saving, which is
          // the same statement: a write is in flight for this row.)
          const busy =
            (setAccountType.isPending && setAccountType.variables?.ids.includes(account.id)) ||
            (setIncludeInCashPosition.isPending &&
              setIncludeInCashPosition.variables?.ids.includes(account.id))
          // The COMPOSITION API rather than `SelectSimple`: an inline cell has
          // no room for a visible label, and `SelectSimpleProps` accepts
          // neither `aria-label` nor `aria-labelledby`, so a bare
          // `SelectSimple` here would announce as an unnamed combobox.
          // `SelectTrigger` passes arbitrary props through (same reason
          // OutstandingChecksPage composes its inline Status select).
          return (
            <ReadOnlyTooltip readOnly={readOnly}>
              <Select
                value={account.account_type}
                onValueChange={(value) =>
                  changeRowAccountType(
                    account.id,
                    value as ConnectionAccountRow['account_type'],
                  )
                }
                disabled={busy || readOnly}
              >
                <SelectTrigger className="h-9 w-[150px]" aria-label={`Type of ${account.name}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accountTypeOptions(account.account_type, account.account_type_api).map(
                    (option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                          {option.label}
                          {/* Marks what the BANK says this account is. The daily
                              refresh no longer rewrites `account_type` (AC#13), so
                              this is the only place a user can see the provider
                              disagreeing with their own choice — and pick it back.

                              IT SHOWS IN THE CLOSED SELECT TOO, and that is KEPT
                              ON PURPOSE (decision 2026-09-18). `SelectValue`
                              renders the selected item's children, badge included,
                              so the Type cell reads "Loan api" whenever the stored
                              type still equals the provider's. That turned out to
                              be the useful signal at a glance: badge present = the
                              bank's classification, untouched; badge absent = a
                              human set this type. Do not "fix" the leak into the
                              trigger — suppressing it would need a separate
                              non-badged render path and would lose that.

                              The visible chip is hidden from assistive tech and the
                              meaning carried as real text, the same treatment as the
                              Pending badge in BankTransactionsTable and the counts in
                              SettingsFacilitiesPage. Without it the option's computed
                              name is the run-together "Credit Cardapi", because the
                              gap between label and chip is CSS, not a text node. */}
                          {option.isApi && (
                            <>
                              {/* Leading space is load-bearing: the name computation concatenates
                                  adjacent nodes without inserting one, so without it the
                                  option announces as "Credit Cardreported by the bank". */}
                              <span className="sr-only">{' reported by the institution'}</span>
                              <span
                                aria-hidden="true"
                                className="rounded bg-muted px-1 text-[10px] font-medium uppercase leading-4 text-muted-foreground"
                              >
                                api
                              </span>
                            </>
                          )}
                        </span>
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </ReadOnlyTooltip>
          )
        },
      },
      {
        key: 'facility_name',
        header: 'Facility',
        headerHint: CONNECTION_COPY.facilityHint,
        sortable: true,
        render: (account) => {
          const busy =
            assignFacility.isPending && assignFacility.variables?.ids.includes(account.id)
          return (
            <ReadOnlyTooltip readOnly={readOnly}>
              <Select
                // Unassigned is a real state, so it is carried by the sentinel
                // rather than by an empty value, which Radix renders as the
                // placeholder and would make "no facility" look like "not set yet".
                value={account.facility_id ?? UNASSIGNED_FACILITY}
                onValueChange={(value) => changeRowFacility(account.id, value)}
                disabled={busy || readOnly}
              >
                <SelectTrigger className="h-9 w-[180px]" aria-label={`Facility of ${account.name}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_FACILITY}>{UNASSIGNED_FACILITY_LABEL}</SelectItem>
                  {facilityOptionsFor(allFacilities ?? [], account.facility_id).map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ReadOnlyTooltip>
          )
        },
      },
      {
        key: 'pack_name',
        header: 'Pack',
        // NOT sortable and NOT editable: a pack is a property of the FACILITY,
        // derived by the read model, so there is nothing on the account to set.
        // (The RPC's sort whitelist has no `pack_name` either.)
        render: (account) => (
          <span className="text-sm text-muted-foreground">{account.pack_name ?? EM_DASH}</span>
        ),
      },
      {
        key: 'include_in_cash_position',
        header: 'Include in Cash Position',
        headerHint: CONNECTION_COPY.cashPositionHint,
        sortable: true,
        render: (account) => (
          <CashPositionToggle
            accountName={account.name}
            accountNumber={account.account_number}
            checked={account.include_in_cash_position}
            locked={isCashPositionLocked(account)}
            busy={
              setIncludeInCashPosition.isPending &&
              setIncludeInCashPosition.variables?.ids.includes(account.id) === true
            }
            readOnly={readOnly}
            onChange={(checked) =>
              setIncludeInCashPosition.mutate({ ids: [account.id], include: checked })
            }
          />
        ),
      },
      {
        key: 'balance',
        header: 'Balance',
        sortable: true,
        cellClassName: 'tabular-nums',
        render: (account) =>
          account.balance == null ? EM_DASH : formatCurrency(account.balance),
      },
      {
        key: 'actions',
        header: 'Actions',
        disableHide: true,
        render: (account) => (
          <ReadOnlyActionMenu
            readOnly={readOnly}
            ariaLabel={`Actions for ${account.name}`}
            items={[
              // Disconnect and Reconnect are mutually exclusive per row: only
              // the one that would change something is offered.
              // `scope: 'row'` — these act on THIS account only, whatever else
              // happens to be ticked; see `confirmPending`.
              account.status === 'disconnected'
                ? {
                    id: 'reconnect',
                    label: 'Reconnect',
                    onClick: () =>
                      setPending({
                        kind: 'reconnect',
                        ids: [account.id],
                        scope: 'row',
                        label: accountConfirmLabel(account),
                      }),
                  }
                : {
                    id: 'disconnect',
                    label: 'Disconnect',
                    onClick: () =>
                      setPending({
                        kind: 'disconnect',
                        ids: [account.id],
                        scope: 'row',
                        label: accountConfirmLabel(account),
                      }),
                  },
              {
                id: 'delete',
                label: 'Delete permanently',
                variant: 'danger' as const,
                onClick: () =>
                  setPending({
                    kind: 'delete',
                    ids: [account.id],
                    scope: 'row',
                    label: accountConfirmLabel(account),
                  }),
              },
            ]}
          />
        ),
      },
    ],
    [
      allFacilities,
      assignFacility,
      changeRowAccountType,
      changeRowFacility,
      readOnly,
      setAccountType,
      setIncludeInCashPosition,
    ],
  )

  /**
   * Scoped, the Pack column goes: every row shares the facility, so it would
   * repeat one value. The Facility column stays — reassigning from here is how
   * an account leaves the facility.
   */
  const visibleColumns = useMemo(
    () => (scope ? columns.filter((column) => column.key !== 'pack_name') : columns),
    [scope, columns],
  )

  /**
   * Bulk actions (AC#18). Facility and type are DROPDOWN actions — the user
   * picks the value, then it is applied to the whole selection; Disconnect and
   * Delete are flat, and Delete routes through the confirmation dialog.
   */
  const bulkActions = useMemo<SelectionAction[]>(
    () => [
      {
        id: 'assign-facility',
        type: 'dropdown',
        label: 'Assign facility',
        options: [
          { value: UNASSIGNED_FACILITY, label: UNASSIGNED_FACILITY_LABEL },
          ...activeFacilities.map((facility) => ({ value: facility.id, label: facility.name })),
        ],
        onSelect: (option) =>
          runBulk(() =>
            assignFacility.mutateAsync({
              ids: selectedIdList,
              facilityId: option.value === UNASSIGNED_FACILITY ? null : option.value,
            }),
          ),
      },
      {
        id: 'set-type',
        type: 'dropdown',
        label: 'Set account type',
        options: ACCOUNT_TYPE_CHOICES.map((choice) => ({
          value: choice.value,
          label: choice.label,
        })),
        // Through `applyAccountType`, so a selection reclassified to Credit
        // Card leaves the Cash Position with it — the bulk path can flip
        // dozens of rows at once, which is the WORST place to leave the
        // DCP-371 invariant broken.
        onSelect: (option) =>
          runBulk(() =>
            applyAccountType(
              selectedIdList,
              option.value as ConnectionAccountRow['account_type'],
            ),
          ),
      },
      {
        id: 'disconnect',
        type: 'action',
        label: 'Disconnect',
        onAction: () =>
          setPending({ kind: 'disconnect', ids: selectedIdList, scope: 'bulk' }),
      },
      {
        id: 'delete',
        type: 'action',
        label: 'Delete',
        destructive: true,
        onAction: () => setPending({ kind: 'delete', ids: selectedIdList, scope: 'bulk' }),
      },
    ],
    [activeFacilities, assignFacility, applyAccountType, selectedIdList, runBulk],
  )

  // Per-user column visibility + order, persisted in localStorage. Keyed
  // 'connection-accounts' so it cannot collide with the other banking tables'
  // saved state. This is also what makes `headerLabel` load-bearing: the menu
  // renders plain text, so a ReactNode `header` without one would show up as
  // "[object Object]".
  //
  // The facility page keeps its OWN layout key: its column set has no Pack, and
  // reconciling the shared layout against it would save Pack as unknown — and
  // bring it back hidden on the organization-wide page.
  const columnPrefs = useTableColumnPrefs(
    scope ? 'connection-accounts-facility' : 'connection-accounts',
    visibleColumns,
  )

  // Reads the VISIBLE search box, not the debounced term: while the debounce is
  // still running the user has already typed something, so "no match for your
  // search" is the honest empty state even though the query has yet to change.
  const hasFilters =
    search.trim().length > 0 ||
    status !== 'connected' ||
    packFilter.length > 0 ||
    facilityFilter.length > 0

  /**
   * What the dialog's title calls its subject. A row action names its account
   * (`account ••••1234`, DCP-404); the bulk bar keeps the count, since a list
   * of masks would not fit a title.
   */
  const pendingSubject = !pending
    ? ''
    : pending.scope === 'row'
      ? `account ${pending.label}`
      : `${pending.ids.length} ${pending.ids.length === 1 ? 'account' : 'accounts'}`
  const isRowPending = pending?.scope === 'row'

  return (
    // A section, not a Card: DataTable draws its own card frame.
    <section className="space-y-4">
      {/* No bottom-padding reservation for the bulk bar: it is `position:
          sticky` (see there), so it takes its own space in the flow at the end
          of this section and cannot cover the last rows. The page's p-6 is
          the gap it lands in. */}
      {/* Section heading with the total as a chip, mirroring the prototype.
          The count lives here rather than beside the filters so it reads as
          "how many this connection has", not as a result-of-filter badge —
          it is the number of rows the filters match, so
          sitting next to them invited exactly that misreading. */}
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold tracking-tight">Accounts &amp; cards</h3>
        {knownTotal !== undefined && (
          <Badge variant="muted">
            {knownTotal} {knownTotal === 1 ? 'account' : 'accounts'}
          </Badge>
        )}
        {headingAction && <div className="ml-auto">{headingAction}</div>}
      </div>

      {/* Toolbar */}
      {/* Every filter carries a VISIBLE label. The prototype leans on
          placeholders alone, but a placeholder is not an accessible name —
          and once a value is picked it is replaced, leaving the control
          unlabelled for everyone. Labelled filters are also what the sibling
          BankingFilters toolbar already does. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Label htmlFor="account-search" className="text-sm text-muted-foreground">
            Search
          </Label>
          <SearchInput
            id="account-search"
            className="mt-1"
            placeholder={CONNECTION_COPY.searchPlaceholder}
            value={search}
            onChange={handleSearchChange}
            clearable
          />
        </div>
        <Select
          value={status}
          onValueChange={(value) => handleStatusChange(value as ConnectionAccountStatusFilter)}
        >
          <SelectTrigger className="w-[150px]" aria-label="Account status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCOUNT_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* MultiSelect ships its own search box and clear (X) — no bespoke
            clear button is added beside it. */}
        {!scope && (
          <>
            <MultiSelect
              label="Pack"
              options={packOptions}
              value={packFilter}
              onChange={handlePackFilterChange}
              placeholder="All packs"
              className="w-[180px]"
              searchable
              searchPlaceholder="Search packs..."
              id="account-pack-filter"
            />
            <MultiSelect
              label="Facility"
              options={facilityOptions}
              value={facilityFilter}
              onChange={handleFacilityFilterChange}
              placeholder="All facilities"
              className="w-[200px]"
              searchable
              searchPlaceholder="Search facilities..."
              id="account-facility-filter"
            />
          </>
        )}
        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters}>
            {CONNECTION_COPY.clearFilters}
          </Button>
        )}
      </div>

      {errorDisplay.banner}
      <DataTable<ConnectionAccountRow>
        columns={visibleColumns}
        data={accounts.rows}
        rowKey={(row) => row.id}
        pagination={dataTablePagination}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        sorting={{ mode: 'server', sortState: shownSort, onSortChange: setSortState }}
        // The table keeps its footer, so a failed page is not a dead end.
        error={errorDisplay.tableError}
        selection={{
          // `mode` is REQUIRED in practice: `useSelection` defaults it to
          // 'none' and then short-circuits every toggle, so omitting it
          // renders the checkboxes and silently ignores every click.
          mode: 'multiple',
          selectedRows: selectedIds,
          // The second argument is always `[]` inside the library, so the
          // selected ROWS are derived from these ids against the loaded
          // page instead of being read from the callback.
          onSelectionChange: setSelectedIds,
          // An inactive facility's page: nothing is selectable, so every
          // row checkbox and the header "select all" are disabled — the
          // bulk bar can never appear — and hovering one says why.
          // Placeholder rows (the previous filter's page, shown while the
          // new one loads) are not selectable either: a row ticked there
          // vanishes when the page lands but would stay in the selection
          // that bulk Disconnect / Delete act on. The same holds during the
          // search debounce, before the typed term reaches the query.
          isRowSelectable: () => !readOnly && !accounts.isPlaceholderData && !searchSettling,
          disabledTooltip: readOnly ? FACILITY_SCOPE_COPY.readOnlyTooltip : undefined,
        }}
        // `bulkActions` is intentionally NOT passed — the floating
        // SelectionActionsBar below is the bar, because two of the actions
        // need a value picked first and `BulkAction` cannot express that.
        emptyState={
          <p className="text-sm text-muted-foreground">
            {hasFilters
              ? CONNECTION_COPY.accountsEmptyFiltered
              : scope
                ? facilityAccountsEmpty(scope.facilityName)
                : CONNECTION_COPY.accountsEmpty}
          </p>
        }
        itemName="accounts"
        // Opens the Columns menu without a table title: the section already
        // carries its heading, and `title` would print a second one.
        showColumnControls
        enableColumnReorder
        {...columnPrefs}
      />

      {/*
        Bulk bar (AC#18). `placement="floating"` is the prototype's dark pill,
        and it is `position: sticky`, so WHERE it renders is load-bearing in
        two ways at once — both satisfied here, and both checked in a real
        browser at 1440x900:
        - it must be inside the page's scroll container. That scroller is
          DashboardLayout's `<main class="flex-1 overflow-y-auto p-6">`; this
          section is inside it, and nothing in between (the page's own
          wrapper) sets `overflow` or `max-h`, which would hijack
          the scrollport. So the pill hovers 28px above the bottom edge of the
          visible page while the rows of a long page scroll past.
        - being laid out in this section's column is what centres it on
          the TABLE: `mx-auto` resolves against this box, not the viewport, so
          it stays centred whether the sidebar is expanded or collapsed. A
          fixed pill would sit ~130px left of the table's centre.
        Last child of the section, which is also the right tab order: keyboard
        focus reaches the bar only after the rows it acts on.

        Fed the selection's size (see `selectedIds`), so it appears the
        moment a row is ticked and counts selected rows on every page. The
        header "select all" covers the CURRENT PAGE only.
      */}
      {!readOnly && (
        <SelectionActionsBar
          selectedCount={selectedIds.size}
          actions={bulkActions}
          onClearSelection={clearSelection}
          itemName="account"
          placement="floating"
        />
      )}

      {/*
        One dialog instance for all three confirmable actions (AC#16/#17), keyed
        off the pending intent rather than a derived row so it survives the rows
        leaving the list on success.
      */}
      <ConfirmationDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !confirmBusy) setPending(null)
        }}
        title={
          pending?.kind === 'disconnect'
            ? `Disconnect ${pendingSubject}?`
            : pending?.kind === 'reconnect'
              ? `Reconnect ${pendingSubject}?`
              : `Delete ${pendingSubject} permanently?`
        }
        description={
          pending?.kind === 'disconnect'
            ? isRowPending
              ? CONNECTION_COPY.disconnectAccountDescription
              : CONNECTION_COPY.disconnectAccountsDescription
            : pending?.kind === 'reconnect'
              ? CONNECTION_COPY.reconnectAccountsDescription
              : isRowPending
                ? CONNECTION_COPY.deleteAccountDescription
                : CONNECTION_COPY.deleteAccountsDescription
        }
        confirmText={
          pending?.kind === 'disconnect'
            ? 'Disconnect'
            : pending?.kind === 'reconnect'
              ? 'Reconnect'
              : 'Delete permanently'
        }
        cancelText="Cancel"
        // Reconnect resumes syncing — it destroys nothing, so it does not get
        // the destructive styling the other two do.
        variant={pending?.kind === 'reconnect' ? 'default' : 'destructive'}
        isLoading={confirmBusy}
        onConfirm={confirmPending}
      />
    </section>
  )
}
